import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import ReactToPrint from 'react-to-print';
import { Orientation } from '@react-pdf/types';
import { Document, Page, pdf, Text, View } from '@react-pdf/renderer';
import cl from 'classnames';
import { CompaniesSummaryRecord } from 'src/models';
import { REPORTS } from 'src/constants';
import {
  useContextData,
  usePage,
  useXlsxWriter,
  XlsxTableConfig,
  useLanguage,
} from 'src/hooks';
import { compareString, currencySign, uuidV4 } from 'src/helper';
import { useAppDispatch } from 'src/store';
import {
  useCompaniesSummary,
  useCompaniesSummaryState,
  fetchCompaniesSummaryAction,
  fetchCompaniesSummaryForExportAction,
} from 'src/services/companiesSummary';
import { clearCompanySummaryState } from 'src/store/reducers/companiesSummary';
import {
  StickyTable,
  PageSwitcher,
  STSortOption,
  StickyTableConfig,
  useStickyCols,
  PrintTable,
  ExportSelect,
  ExportModal,
  ExportMode,
  getExportDates,
  PdfTableParams,
  PdfTableView,
  PdfModal,
  PdfTablePreparer,
} from 'src/components';
import {
  Button,
  FilterModal,
  FilterModalButton,
  FilterModalIndicator,
  useDateRangePicker,
} from 'src/components/general';
import styles from './reportsBillingStyle.module.scss';

export const ReportsBilling = () => {
  const {
    header: { width: headerWidth },
  } = useContextData();
  const {
    access: { accessMap },
  } = usePage();
  const dispatch = useAppDispatch();
  const { t, currentLang } = useLanguage();
  const state = useCompaniesSummaryState();
  const companiesSummaryRecords = useCompaniesSummary();
  const { writeToXlsx } = useXlsxWriter<CompaniesSummaryRecord>();

  const [isLoading, setIsLoading] = useState(state.isPending);
  const [isFiltered, setIsFiltered] = useState(false);

  const {
    range,
    initRange,
    dateRangePicker,
    modalDateRangePicker,
    applyDRPChanges,
    resetDRPChanges,
    resetDRPToInit,
    clearDRP,
  } = useDateRangePicker({
    inputClassName: styles.datepickerInput,
    modalInputClassName: styles.datepickerModalInput,
    disabled: isLoading,
  });

  const rangeIndicatorText = useMemo(
    () =>
      currentLang && range.start && range.end ? (
        <>
          <span className={styles.nowrap}>{range.start.localString}</span>
          {' - '}
          <span className={styles.nowrap}>{range.end.localString}</span>
        </>
      ) : undefined,
    [range.end, range.start, currentLang],
  );

  const [exportData, setExportData] = useState<{
    uuid: string;
    filename: string;
    records: CompaniesSummaryRecord[];
  } | null>(null);
  const [exportReqUuid, setExportReqUuid] = useState<string | null>(null);

  const fetchExportCompaniesSummary = useCallback(
    (mode: ExportMode) => {
      const dates = getExportDates(mode);
      const uuid = uuidV4();
      setExportReqUuid(uuid);

      dispatch(
        fetchCompaniesSummaryForExportAction({
          params: {
            start: dates[0].format,
            end: dates[1].format,
          },
        }),
      ).then((res) => {
        if (fetchCompaniesSummaryForExportAction.fulfilled.match(res)) {
          setExportData({
            uuid,
            filename: `reports_billing_${dates[0].localString}_-_${dates[1].localString}.xlsx`,
            records: res.payload,
          });
        }

        if (fetchCompaniesSummaryForExportAction.rejected.match(res)) {
          setExportReqUuid(null);
        }
      });
    },
    [dispatch],
  );

  const [showFiltersModal, setShowFiltersModal] = useState(false);
  const [sortOption, setSortOption] = useState<STSortOption | null>(null);
  const printRef = useRef<HTMLDivElement>(null);

  const [showExportPdf, setShowExportPdf] = useState(false);
  const [isPdfLoading, setIsPdfLoading] = useState(false);
  const [pdfTableProps, setPdfTableProps] = useState<{
    isActive: boolean;
    orientation?: Orientation;
    params: PdfTableParams | null;
  }>({ isActive: false, params: null });
  const [pdfBlob, setPdfBlob] = useState<Blob | null>(null);

  const xlsxFileName = useMemo(() => {
    const rangePart =
      range.start && range.end
        ? `_${range.start.localString}_-_${range.end.localString}`
        : '';

    return `reports_billing${rangePart}.xlsx`;
  }, [range]);

  const pdfFileName = useMemo(() => {
    const rangePart =
      range.start && range.end
        ? `_${range.start.localString}_-_${range.end.localString}`
        : '';

    return `reports_billing${rangePart}.pdf`;
  }, [range]);

  const rangePrintText = useMemo(() => {
    if (!range.start || !range.end) {
      return null;
    }

    return (
      <div className={styles.printText}>
        <span>{t('general.interval')}</span>
        {range.start.localString} - {range.end.localString}
      </div>
    );
  }, [range, t]);

  const rangePfdText = useMemo(() => {
    if (!range.start || !range.end) {
      return null;
    }

    return (
      <View
        style={{
          width: '100%',
          display: 'flex',
          flexDirection: 'row',
        }}
      >
        <View style={{ marginRight: '3pt' }}>
          <Text
            style={{
              fontFamily: 'Inter',
              fontSize: 12,
              fontWeight: 600,
            }}
          >
            {t('general.interval')}
          </Text>
        </View>
        <View style={{ marginRight: '3pt' }}>
          <Text
            style={{
              fontFamily: 'Inter',
              fontSize: 12,
              fontWeight: 400,
            }}
          >
            {range.start.localString} - {range.end.localString}
          </Text>
        </View>
      </View>
    );
  }, [range, t]);

  const isRangeChanged = useMemo(
    () =>
      range.start?.time !== initRange.start?.time ||
      range.end?.time !== initRange.end?.time,
    [
      initRange.start?.time,
      initRange.end?.time,
      range.start?.time,
      range.end?.time,
    ],
  );

  useEffect(() => {
    let start: string | undefined = undefined;
    let end: string | undefined = undefined;

    if (range.start && range.end) {
      start = range.start.format;
      end = range.end.format;
    }

    setIsLoading(true);
    setIsFiltered(isRangeChanged);

    dispatch(
      fetchCompaniesSummaryAction({
        params: {
          start,
          end,
        },
      }),
    );
  }, [dispatch, range, isRangeChanged]);

  useEffect(() => {
    setIsLoading(state.isPending);
  }, [state.isPending]);

  useEffect(
    () => () => {
      dispatch(clearCompanySummaryState());
    },
    [dispatch],
  );

  const getSummaryRecords = useCallback(
    (records: CompaniesSummaryRecord[]): CompaniesSummaryRecord[] =>
      records.map((record) => {
        const detail = record.details[0];
        const comm = (detail?.agentCommission || 0) / 100;

        return {
          ...record,
          legalEntityName: detail?.legalEntityName || '---',
          endUsersTotal: record.total - record.vip,
          agentTotal: detail
            ? record.total * comm - record.vip * (1 - comm)
            : '---',
        };
      }),
    [],
  );

  const summaryRecords: CompaniesSummaryRecord[] = useMemo(
    () => getSummaryRecords(companiesSummaryRecords),
    [companiesSummaryRecords, getSummaryRecords],
  );

  const xlsxConfig: XlsxTableConfig<CompaniesSummaryRecord>[] = useMemo(
    () => [
      {
        title: t('page.reports.billing.legalEntityName'),
        field: 'legalEntityName',
        key: 'legalEntityName',
      },
      {
        title: t('page.reports.billing.name'),
        field: 'name',
        key: 'name',
      },
      {
        title: t('page.reports.billing.headsets'),
        field: 'headsets',
        key: 'headsets',
      },
      {
        title: t('page.reports.billing.endUsersTotal'),
        field: 'endUsersTotal',
        key: 'endUsersTotal',
        render: (value) => `${value} ${currencySign('rub')}`,
      },
      {
        title: t('page.reports.billing.vip'),
        field: 'vip',
        key: 'vip',
        render: (value) => `${value} ${currencySign('rub')}`,
      },
      {
        title: t('page.reports.billing.total'),
        field: 'total',
        key: 'total',
        render: (value) => `${value} ${currencySign('rub')}`,
      },
      {
        title: t('page.reports.billing.agentTotal'),
        field: 'agentTotal',
        key: 'agentTotal',
        render: (value) =>
          typeof value === 'string'
            ? value
            : `${parseFloat(value).toFixed(2)} ${currencySign('rub')}`,
      },
    ],
    [t],
  );

  const config: StickyTableConfig<CompaniesSummaryRecord>[] = useMemo(
    () => [
      {
        title: t('page.reports.billing.legalEntityName'),
        field: 'legalEntityName',
        key: 'legalEntityName',
        sorter: (a, b, sortOrder) =>
          compareString(a.legalEntityName, b.legalEntityName) *
          (sortOrder === 'descend' ? -1 : 1),
      },
      {
        title: t('page.reports.billing.name'),
        field: 'name',
        key: 'name',
        sorter: (a, b, sortOrder) =>
          compareString(a.name, b.name) * (sortOrder === 'descend' ? -1 : 1),
      },
      {
        title: t('page.reports.billing.headsets'),
        field: 'headsets',
        key: 'headsets',
        sorter: (a, b, sortOrder) => {
          if (a.headsets === b.headsets) {
            return 0;
          }
          return (a.headsets - b.headsets) * (sortOrder === 'descend' ? -1 : 1);
        },
      },
      {
        title: t('page.reports.billing.endUsersTotal'),
        field: 'endUsersTotal',
        key: 'endUsersTotal',
        sorter: (a, b, sortOrder) => {
          if (a.endUsersTotal === b.endUsersTotal) {
            return 0;
          }
          return (
            (a.endUsersTotal - b.endUsersTotal) *
            (sortOrder === 'descend' ? -1 : 1)
          );
        },
        render: (value) => `${value} ${currencySign('rub')}`,
      },
      {
        title: t('page.reports.billing.vip'),
        field: 'vip',
        key: 'vip',
        sorter: (a, b, sortOrder) => {
          if (a.vip === b.vip) {
            return 0;
          }
          return (a.vip - b.vip) * (sortOrder === 'descend' ? -1 : 1);
        },
        render: (value) => `${value} ${currencySign('rub')}`,
      },
      {
        title: t('page.reports.billing.total'),
        field: 'total',
        key: 'total',
        sorter: (a, b, sortOrder) => {
          if (a.total === b.total) {
            return 0;
          }
          return (a.total - b.total) * (sortOrder === 'descend' ? -1 : 1);
        },
        render: (value) => `${value} ${currencySign('rub')}`,
      },
      {
        title: t('page.reports.billing.agentTotal'),
        field: 'agentTotal',
        key: 'agentTotal',
        sorter: (a, b, sortOrder) => {
          if (a.agentTotal === b.agentTotal) {
            return 0;
          }

          if (a.agentTotal !== '---' && b.agentTotal !== '---') {
            return (
              (a.agentTotal - b.agentTotal) * (sortOrder === 'descend' ? -1 : 1)
            );
          }

          return (
            (a.agentTotal === '---' ? -1 : 1) *
            (sortOrder === 'descend' ? -1 : 1)
          );
        },
        render: (value) =>
          typeof value === 'string'
            ? value
            : `${parseFloat(value).toFixed(2)} ${currencySign('rub')}`,
      },
    ],
    [t],
  );

  const {
    stickyCols,
    stickyColsFilter,
    modalStickyColsFilter,
    applySCChanges,
    resetSCChanges,
  } = useStickyCols({
    config,
    className: styles.stickyCols,
    modalClassName: styles.modalStickyCols,
  });

  useEffect(() => {
    if (!exportData || exportData.uuid !== exportReqUuid) {
      return;
    }

    setExportReqUuid(null);

    writeToXlsx(
      { config: xlsxConfig, data: getSummaryRecords(exportData.records) },
      exportData.filename,
    );
  }, [exportData, exportReqUuid, getSummaryRecords, writeToXlsx, xlsxConfig]);

  const onPdfHandle = useCallback((orientation: Orientation) => {
    setIsPdfLoading(true);
    setPdfTableProps({
      isActive: true,
      params: null,
      orientation,
    });
  }, []);

  useEffect(() => {
    if (pdfTableProps.isActive || !pdfTableProps.params) {
      return;
    }

    pdf(
      <Document>
        <Page
          size="A4"
          style={{
            flexDirection: 'column',
            backgroundColor: '#FFF',
            padding: '28pt 28pt',
          }}
          orientation={pdfTableProps.orientation}
          dpi={96}
        >
          {rangePfdText}
          <PdfTableView
            params={pdfTableProps.params}
            style={{ marginTop: '6pt' }}
          />
        </Page>
      </Document>,
    )
      .toBlob()
      .then((blob) => {
        setPdfBlob(blob);
        setIsPdfLoading(false);
      });
  }, [
    pdfTableProps.isActive,
    pdfTableProps.orientation,
    pdfTableProps.params,
    rangePfdText,
  ]);

  return (
    <div className={styles.container}>
      <div className={styles.printHidden}>
        <div className={styles.printWrapper} ref={printRef}>
          <div className={styles.printArea}>
            {rangePrintText}
            <PrintTable
              config={config}
              data={summaryRecords}
              sortOption={sortOption}
              className={styles.printTable}
            />
          </div>
        </div>
      </div>
      <FilterModal
        show={showFiltersModal}
        close={() => {
          setShowFiltersModal(false);
        }}
        onApply={() => {
          applySCChanges();
          applyDRPChanges();
          setShowFiltersModal(false);
        }}
        onCancel={() => {
          resetSCChanges();
          resetDRPChanges();
        }}
      >
        {modalDateRangePicker}
        {modalStickyColsFilter}
      </FilterModal>
      <ExportModal
        show={Boolean(exportReqUuid)}
        close={() => {
          setExportReqUuid(null);
        }}
      />
      <PdfModal
        show={showExportPdf}
        close={() => {
          setShowExportPdf(false);
          setIsPdfLoading(false);
          setPdfTableProps({ isActive: false, params: null });
        }}
        pdfBlob={pdfBlob}
        fileName={pdfFileName}
        isLoading={isPdfLoading}
        onPdfHandle={onPdfHandle}
      />
      <div
        className={styles.topSection}
        style={{
          minWidth: headerWidth,
          maxWidth: headerWidth,
        }}
      >
        <div
          className={cl(
            styles.pageSwitcherContainer,
            accessMap[REPORTS].length === 1 && styles.inactive,
          )}
        >
          <PageSwitcher
            className={styles.pageSwitcher}
            selectClassName={styles.mobileSelect}
          />
          <div className={styles.filterButtons}>
            <FilterModalButton
              setShow={setShowFiltersModal}
              badge={Boolean(rangeIndicatorText)}
              disabled={isLoading}
            />
            <ExportSelect
              onExport={(mode) => {
                if (mode === 'selected') {
                  writeToXlsx(
                    { config: xlsxConfig, data: summaryRecords },
                    xlsxFileName,
                  );
                } else {
                  fetchExportCompaniesSummary(mode);
                }
              }}
              className={styles.filterIcon}
              isDisabled={isLoading}
              small
            />
            <FilterModalButton
              setShow={() => {
                setShowExportPdf(true);
                onPdfHandle('portrait');
              }}
              icon="exportToPdf"
              disabled={isLoading}
            />
            <ReactToPrint
              content={() => printRef.current}
              trigger={() => (
                <button
                  className={styles.filterButton}
                  disabled={Boolean(isLoading)}
                >
                  <svg
                    width="23"
                    height="23"
                    viewBox="0 0 23 23"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M5.19982 17.8002H3.09991C2.54298 17.8002 2.00886 17.5789 1.61505 17.1851C1.22124 16.7913 1 16.2572 1 15.7003V10.4505C1 9.89356 1.22124 9.35944 1.61505 8.96564C2.00886 8.57183 2.54298 8.35059 3.09991 8.35059H19.8992C20.4561 8.35059 20.9902 8.57183 21.384 8.96564C21.7778 9.35944 21.9991 9.89356 21.9991 10.4505V15.7003C21.9991 16.2572 21.7778 16.7913 21.384 17.1851C20.9902 17.5789 20.4561 17.8002 19.8992 17.8002H17.7993"
                      stroke="white"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M17.8006 13.6006H5.20117V22.0002H17.8006V13.6006Z"
                      stroke="white"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M5.20117 8.34968V1H17.8006V8.34968"
                      stroke="white"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              )}
            />
            <FilterModalIndicator
              icon="calendar"
              text={rangeIndicatorText}
              clear={clearDRP}
              disabled={isLoading}
            />
          </div>
        </div>
        <div className={styles.filtersWrapper}>
          <div className={styles.filters}>
            {dateRangePicker}
            {stickyColsFilter}
          </div>
          <div className={styles.buttons}>
            <ExportSelect
              onExport={(mode) => {
                if (mode === 'selected') {
                  writeToXlsx(
                    { config: xlsxConfig, data: summaryRecords },
                    xlsxFileName,
                  );
                } else {
                  fetchExportCompaniesSummary(mode);
                }
              }}
              className={styles.button}
              isDisabled={isLoading}
            />
            <Button
              label={t('component.pdf.export')}
              design="lightBlue"
              className={styles.button}
              icon="exportToPdf"
              onClick={() => {
                setShowExportPdf(true);
                onPdfHandle('portrait');
              }}
              disabled={Boolean(isLoading)}
            />
            <ReactToPrint
              content={() => printRef.current}
              trigger={() => (
                <Button
                  label={t('general.print')}
                  design="lightBlue"
                  className={styles.button}
                  icon="print"
                  disabled={Boolean(isLoading)}
                />
              )}
            />
          </div>
        </div>
      </div>
      <StickyTable
        config={config}
        data={summaryRecords}
        isLoading={isLoading}
        stickyCols={stickyCols}
        className={styles.table}
        onSort={(value) => {
          setSortOption(value);
        }}
        isFiltered={isFiltered}
        resetFilter={resetDRPToInit}
        notFoundText={t('general.resetFilters')}
      />
      <PdfTablePreparer
        isActive={pdfTableProps.isActive}
        config={config}
        data={summaryRecords}
        sortOption={sortOption}
        onRender={(params) => {
          setPdfTableProps((props) => ({ ...props, isActive: false, params }));
        }}
      />
    </div>
  );
};

export default ReportsBilling;
